import { localeAwareFormat } from 'app/lib/date';
import { ListingSubtype, type Listing } from 'app/types/listing';
import { differenceInCalendarDays, startOfDay } from 'date-fns';

// API listing distances are expressed in metres.
const NEARBY_LISTING_THRESHOLD_METRES = 2000;

export function isListingNearby(distance: number | undefined) {
  return (
    typeof distance === 'number' &&
    Number.isFinite(distance) &&
    distance >= 0 &&
    distance <= NEARBY_LISTING_THRESHOLD_METRES
  );
}

const COUNTDOWN_THRESHOLD_DAYS = 7;
const NEW_LISTING_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

export function isOfferListingNew(createdAt: string, now = Date.now()) {
  const createdAtTimestamp = new Date(createdAt).getTime();
  return createdAtTimestamp > now - NEW_LISTING_THRESHOLD_MS && createdAtTimestamp <= now;
}

function formatCardDate(date: Date) {
  return localeAwareFormat(date, 'd MMM');
}

export function getDateTag(listing: Pick<Listing, 'start' | 'end' | 'subtype' | 'urgent'>, now = new Date()) {
  const today = startOfDay(now);
  const start = listing.start ? startOfDay(new Date(listing.start)) : null;
  const end = listing.end ? startOfDay(new Date(listing.end)) : null;

  if (listing.urgent && listing.subtype === ListingSubtype.expirable) {
    const remainingDays = end ? differenceInCalendarDays(end, today) : null;
    return {
      key: remainingDays !== null && remainingDays > 0 ? 'dateTag.urgentRemaining' : 'dateTag.urgent',
      values: remainingDays !== null && remainingDays > 0 ? { count: remainingDays } : {},
      urgent: true,
    } as const;
  }

  if (start && !end) {
    return differenceInCalendarDays(start, today) > 0
      ? { key: 'dateTag.from', values: { date: formatCardDate(start) }, urgent: false }
      : null;
  }

  if (!start && end) {
    const remainingDays = differenceInCalendarDays(end, today);
    if (remainingDays < 0) return null;
    return remainingDays <= 1
      ? { key: 'dateTag.remaining', values: { count: Math.max(1, remainingDays) }, urgent: false }
      : { key: 'dateTag.until', values: { date: formatCardDate(end) }, urgent: false };
  }

  if (start && end) {
    const remainingDays = differenceInCalendarDays(end, today);
    if (remainingDays < 0) return null;
    if (differenceInCalendarDays(start, today) <= 0 && remainingDays <= COUNTDOWN_THRESHOLD_DAYS) {
      return { key: 'dateTag.remaining', values: { count: Math.max(1, remainingDays) }, urgent: false };
    }
    return {
      key: 'dateTag.range',
      values: {
        start: formatCardDate(start),
        end: formatCardDate(end),
      },
      urgent: false,
    };
  }

  return null;
}
